import React from 'react';

import { Document, Page, Text, View } from '@react-pdf/renderer';

import TablePDF from './TablePDF';
import KeyValuePDF from '../shared/KeyValuePDF';
import GlosaryColorsPDF from '../shared/GlosaryColorsPDF';
import GlosaryAbreviationsPDF from '../shared/GlosaryAbreviationsPDF';
import DividerPDF from '../shared/DividerPDF';
import { mapReportData } from './mapReportData';
import { getTablePDFStyle } from '../../constants';
import EmptyUserDataPDF from '../shared/EmptyUserDataPDF';

/**
 * @typedef {Object} ReportData
 * @property {string} id - Unique identifier for the report item.
 * @property {Array<Object>} source - The data source for the table in the report.
 */

/**
 * @typedef {Object} KeyValueData
 * @property {string} label - The label for the key-value pair.
 * @property {string} value - The value for the key-value pair.
 */

/**
 * @typedef {Object} ReportPDFProps
 * @property {string} [title=''] - The title of the report.
 * @property {Array<ReportData>} [data=[]] - The data for the report.
 * @property {Array<KeyValueData>} [employeeData=[]] - The employee data to be displayed in the report.
 * @property {Array<KeyValueData>} [employerData=[]] - The employer data to be displayed in the report.
 * @property {Array<Object>} columns - The columns for the table in the report.
 */

const ReportPDF = ({
  data = {},
  orientation = 'portrait',
  title,
  includeGlosary = true,
  emptyUserDataLines = [],
}) => {
  const styles = getTablePDFStyle();

  const tableItems = mapReportData(
    data,
    ({ id, employee, employer, columns, source, summary, footer }) => {
      const columnsToRemove = ['id'];
      const columnsToKeep = columns.filter(
        column => !columnsToRemove.includes(column.dataIndex),
      );
      const summaryToKeep = [
        ...summary,
        ...Array(Math.max(0, columnsToKeep.length - summary.length)).fill(''),
      ];
      return { id, employee, employer, columnsToKeep, source, summaryToKeep, footer };
    },
  );

  const renderTable = ({ id, employee, employer, columnsToKeep, source, summaryToKeep, footer }) => (
    <View key={id} style={styles.cardReport}>
      <TablePDF
        headerSection={
          <View>
            <View style={styles.infoHeader}>
              {employee.map(({ label, value }) => (
                <KeyValuePDF key={label} label={label} value={value} />
              ))}
            </View>
            {employer.length > 0 && <DividerPDF />}
            <View style={styles.infoHeader}>
              {employer.map(({ label, value }) => (
                <KeyValuePDF key={label} label={label} value={value} />
              ))}
            </View>
          </View>
        }
        key={id}
        data={source}
        headers={columnsToKeep}
        summary={summaryToKeep}
        footer={footer}
      />
    </View>
  );

  const [firstTable, ...restTables] = tableItems;

  return (
    <Document>
      {/* Page 1: glossary + first table */}
      <Page size="A4" orientation={orientation} style={styles.page}>
        <Text style={styles.title}>{title}</Text>
        {includeGlosary && (
          <View>
            <GlosaryAbreviationsPDF />
            <GlosaryColorsPDF />
          </View>
        )}
        {firstTable ? (
          renderTable(firstTable)
        ) : (
          <EmptyUserDataPDF lines={emptyUserDataLines} styles={styles} />
        )}
      </Page>

      {/* One page per remaining table */}
      {restTables.map(item => (
        <Page key={item.id} size="A4" orientation={orientation} style={styles.page}>
          {renderTable(item)}
        </Page>
      ))}
    </Document>
  );
};

export default ReportPDF;
